import React, {useMemo} from 'react';

import {Explanation, SampleResponse} from '../shared/ContentBlocks';
import type {QuestionComponentProps} from '../shared/QuestionComponent/QuestionComponent.react';
import {QuestionComponent} from '../shared/QuestionComponent/QuestionComponent.react';
import RubricPreview from '../shared/RubricPreview/RubricPreview';

import {FreeResponseInputArea, FreeResponseInputAreaProvider} from './FreeResponseInputArea.react';
import type {FrqGrading} from './frqGradingStatus';

type FreeResponseQuestionComponentProps = QuestionComponentProps & {
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
  /**
   * The viewed guess's grading state, where a teacher-gated read supplies one. Absent in
   * independent practice and on a student's own view, which have no grading to report.
   */
  frqGrading?: FrqGrading;
};

const FreeResponseQuestionComponent = (props: FreeResponseQuestionComponentProps) => {
  // QuestionComponentProps declares none of these, so they must not reach the spread below.
  const {isViewingStudentWork, frqGrading, ...questionComponentProps} = props;
  const {activeQuestion, activeQuestionStore, guess, questionState} = questionComponentProps;
  const storeName = activeQuestionStore?.getName();

  // The rubric the backend exposes to students once they've submitted — the same source the
  // unscored RubricPreview reads below.
  const questionRubric = activeQuestion?.getMeta?.().getQuestionRubric?.();
  const inputAreaValue = useMemo(
    () => ({
      activeQuestion,
      storeName,
      guess,
      questionState,
      questionRubric,
      isViewingStudentWork,
      frqGrading
    }),
    [
      activeQuestion,
      storeName,
      guess,
      questionState,
      questionRubric,
      isViewingStudentWork,
      frqGrading
    ]
  );

  const customExplanation = (
    <>
      <SampleResponse question={activeQuestion} />
      {/* The structured rubric replaces the free-text explanation, falling back to it when
          there's no criteria rubric. The rubric lives in answer-key meta (question_rubric),
          which the backend only populates after a submitted guess. */}
      <RubricPreview
        rubric={activeQuestion?.getMeta?.().getQuestionRubric?.()}
        fallback={<Explanation question={activeQuestion} />}
      />
    </>
  );

  return (
    <FreeResponseInputAreaProvider value={inputAreaValue}>
      <QuestionComponent
        {...questionComponentProps}
        customExplanation={customExplanation}
        customHOA={<></>}
        customResultIndicator={<></>}
        ActiveQuestionInputArea={FreeResponseInputArea}
      />
    </FreeResponseInputAreaProvider>
  );
};

type FreeResponseQuestionTypeProps = Omit<FreeResponseQuestionComponentProps, 'questionState'>;

const FreeResponseQuestion = {
  PreGuess: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='PreGuess' />
  ),
  PostGuess: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='PostGuess' />
  ),
  Dennis: (props: FreeResponseQuestionTypeProps) => (
    <FreeResponseQuestionComponent {...props} questionState='Dennis' />
  )
};

export {FreeResponseQuestion};
